import React, { useEffect, useRef, useState } from 'react';
import { Info, TrendingUp, TrendingDown } from 'lucide-react';
import { Translate } from 'react-redux-i18n';
import CurrencyHelper from '../../../../helpers/currencyHelper';

export interface MetricCardProps {
  label: string | React.ReactNode;
  value: number | string;
  formatValue?: (value: number) => string | React.ReactNode;
  previousValue?: number;
  icon?: React.ReactNode;
  tooltip?: string;
  yoyChange?: number; // Percentage change
  colorClass?: string; // For styling (selected, clickable, etc.)
  isAnimating?: boolean; // Whether to animate value changes
  isCurrency?: boolean;
  currency?: string; // ISO currency code used when isCurrency is set
  isPercentage?: boolean;
  isAggregated?: boolean; // Show aggregation indicator
  onClick?: () => void; // Click handler for metric selection
}

export const MetricCard: React.FC<MetricCardProps> = ({
  label,
  value,
  formatValue,
  previousValue,
  icon,
  tooltip,
  yoyChange,
  colorClass,
  isAnimating = false,
  isCurrency = false,
  currency,
  isPercentage = false,
  isAggregated = false,
  onClick
}) => {
  const [displayValue, setDisplayValue] = useState<number>(
    typeof value === 'number' ? value : 0
  );
  const animationFrameRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const startValueRef = useRef<number>(displayValue);

  useEffect(() => {
    if (!isAnimating || typeof value !== 'number') {
      setDisplayValue(typeof value === 'number' ? value : 0);
      return;
    }

    const targetValue = value;
    const startValue = displayValue;
    const duration = 800; // ms

    startValueRef.current = startValue;
    startTimeRef.current = null;

    const animate = (timestamp: number) => {
      if (!startTimeRef.current) {
        startTimeRef.current = timestamp;
      }

      const elapsed = timestamp - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentValue = startValueRef.current + (targetValue - startValueRef.current) * easeOut;

      setDisplayValue(currentValue);

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [value, isAnimating]);

  // Helper function to split currency into major and minor parts for styling
  const splitCurrencyParts = (formatted: string): { major: string; minor: string } => {
    // Match pattern: currency symbol + amount + decimal separator + cents
    const match = formatted.match(/^(.*?)([\.,])(\d{2})$/);
    if (!match) {
      return { major: formatted, minor: '' };
    }
    const [, major, separator, cents] = match;
    return { major, minor: `${separator}${cents}` };
  };

  // Format the value
  let formattedValue: string | React.ReactNode;
  if (formatValue) {
    formattedValue = formatValue(displayValue);
  } else if (isCurrency) {
    const currencyString = CurrencyHelper.formatCurrency(displayValue, currency, { alwaysShowDecimals: true });
    const { major, minor } = splitCurrencyParts(currencyString);
    
    formattedValue = minor ? (
      <>
        {major}
        <span className="franchise-metric-value-minor">{minor}</span>
      </>
    ) : currencyString;
  } else if (isPercentage) {
    formattedValue = `${displayValue.toFixed(1)}%`;
  } else {
    formattedValue = displayValue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  return (
    <div 
      className={`franchise-metric-card ${colorClass || ''}`}
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      <div className="franchise-metric-header">
        <span className="franchise-metric-label">
          {label}
          {isAggregated && (
            <span title="Aggregated across multiple entities">
              <Info className="franchise-metric-aggregated-icon" />
            </span>
          )}
        </span>
        {tooltip && (
          <span title={tooltip}>
            <Info className="franchise-metric-tooltip-icon" />
          </span>
        )}
      </div>

      <div className="franchise-metric-value-row">
        {icon && <div className="franchise-metric-icon">{icon}</div>}
        <div className="franchise-metric-value">{formattedValue}</div>
      </div>

      {yoyChange !== undefined && (
        <div className={`franchise-metric-yoy ${yoyChange >= 0 ? 'positive' : 'negative'}`}>
          {yoyChange >= 0 ? (
            <TrendingUp className="franchise-metric-yoy-icon" />
          ) : (
            <TrendingDown className="franchise-metric-yoy-icon" />
          )}
          <span>{Math.abs(yoyChange).toFixed(1)}%</span>
        </div>
      )}
    </div>
  );
};
